// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { currencySchema } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';
import { Shipment, shipmentSchema } from '@/shipment/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

const shipShipmentResponseSchema = z.object({
  data: shipmentSchema,
  metadata: responseMetadataSchema,
});

/**
 * Schema for ship items in the request.
 * Uses unified itemId for both parts and products.
 */
const shipItemSchema = z.object({
  itemId: z.string().uuid(),
  quantity: z.number().int().positive().optional(),
  externalReference: z.string().optional(),
  serials: z.array(z.string()).optional(),
});

export const shipShipmentSchema = z.object({
  shipLocationId: z.string(),
  items: z.array(shipItemSchema),
  shipmentNote: z.string().optional(),
  shippedAt: dateTimeSchema,
  shippedAtTimezone: z.string().optional(),
  shippingCost: currencySchema.nullable().optional(),
});

export type ShipShipmentRequest = z.infer<typeof shipShipmentSchema>;

/**
 * Ship a shipment with the specified details.
 *
 * @param shipmentKey - The key from the shipment
 * @param request - The shipment details to ship
 * @returns The shipped shipment
 */
export async function shipShipment(
  shipmentKey: string,
  request: ShipShipmentRequest
): Promise<Shipment> {
  const url = `/v1/shipment/${shipmentKey}/ship`;
  const response = await api.post(url, {
    ...request,
    shippingCost: serializeOptionalCurrencyForAPI(request.shippingCost),
  });

  const { data } = await parseResponse(
    shipShipmentResponseSchema,
    response.data,
    {
      schemaName: 'ShipShipment',
      endpoint: url,
    }
  );
  return data;
}
